"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.swapTX = exports.SwappaManager = void 0;
const connect_1 = require("@celo/connect");
const async_1 = require("@celo/utils/lib/async");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const SwappaRouterV1_1 = require("../types/web3-v1-contracts/SwappaRouterV1");
const multicall_1 = require("./multicall");
const atoken_v2_1 = require("./pairs/atoken-v2");
const stableswap_1 = require("./pairs/stableswap");
const symmetricswap_1 = require("./pairs/symmetricswap");
const uniswapv2_1 = require("./pairs/uniswapv2");
const router_1 = require("./router");
function parsePairTypeToPair({ _type: pairType, tokenA, tokenB, poolAddress, lpToken }, web3, chainId = 42220) {
    switch (pairType) {
        case "atoken-v2":
            return new atoken_v2_1.PairATokenV2(chainId, web3, poolAddress, tokenB, tokenA);
        case "balancer-v1":
            return new symmetricswap_1.PairSymmetricSwap(chainId, web3, poolAddress, tokenA, tokenB);
        case "stableswap":
            return new stableswap_1.PairStableSwap(chainId, web3, poolAddress, lpToken);
        case "uni-v2":
            return new uniswapv2_1.PairUniswapV2(chainId, web3, poolAddress, undefined, lpToken);
        default:
            return undefined;
    }
}
function addPairToMap(pairsByToken, pair) {
    for (const token of [pair.tokenA, pair.tokenB]) {
        const arr = pairsByToken.get(token);
        if (arr)
            arr.push(pair);
        else
            pairsByToken.set(token, [pair]);
    }
}
function calcIntervals(arrayLengths) {
    let last = 0;
    return arrayLengths.map((len) => {
        const interval = [last, last + len];
        last = last + len;
        return interval;
    });
}
class SwappaManager {
    constructor(kit, routerAddr, registries, web3) {
        this.kit = kit;
        this.routerAddr = routerAddr;
        this.registries = registries;
        this.web3 = web3;
        this.pairs = [];
        this.pairsByToken = new Map();
        this.pairsByRegistry = new Map();
        this.pairsByLP = new Map();
        this.multicallIntervals = [];
        this.bulkRefreshPairs = async (pairBootInfo) => {
            var _a;
            let multicallData = this.multiCallData;
            let multiCallPayloadSlice = (_a = this.multiCallPayloadSlice) !== null && _a !== void 0 ? _a : new Array(this.pairs.length);
            if (!multicallData) {
                multicallData = this.pairs.flatMap((pair, i) => {
                    const payloads = pair.getMulticallPayloadForBootstrap();
                    multiCallPayloadSlice[i] = payloads.length;
                    return payloads;
                });
                this.multiCallData = multicallData;
                this.multiCallPayloadSlice = multiCallPayloadSlice;
                this.multicallIntervals = calcIntervals(multiCallPayloadSlice);
            }
            const result = await (0, multicall_1.multicallMultipleContractMultipleData)(this.web3, multicallData);
            const concurrentRefresh = async (val, p) => {
                var _a, _b, _c;
                const [start, stop] = this.multicallIntervals[p];
                if (start === stop)
                    return;
                const pair = this.pairs[p];
                const bootPayload = (_a = pairBootInfo === null || pairBootInfo === void 0 ? void 0 : pairBootInfo[p]) !== null && _a !== void 0 ? _a : {};
                for (let i = start; i < stop; i++) {
                    bootPayload[(_c = (_b = this.multiCallData) === null || _b === void 0 ? void 0 : _b[i].fieldName) !== null && _c !== void 0 ? _c : ""] = result[i];
                }
                pair.bootstrap(bootPayload);
            };
            await (0, async_1.concurrentMap)(50, this.pairs, concurrentRefresh);
        };
        this.bulkInitializePairs = async (pairBootInfo) => {
            this.pairs = pairBootInfo.map((info) => {
                const pair = parsePairTypeToPair(info, this.web3, 42220);
                for (const token of [info.tokenA, info.tokenB]) {
                    const arr = this.pairsByToken.get(token);
                    if (arr)
                        arr.push(pair);
                    else
                        this.pairsByToken.set(token, [pair]);
                }
                if (info.lpToken) {
                    this.pairsByLP.set(info.lpToken, pair);
                }
                return pair;
            });
            await this.bulkRefreshPairs(pairBootInfo);
        };
        this.supportsTokens = (tokenIn, tokenOut) => this.pairsByToken.has(tokenIn) &&
            (!tokenOut || this.pairsByToken.has(tokenOut));
        this.supportsLp = (lp) => this.pairsByLP.has(lp);
        this.getPairForLP = (lp) => this.pairsByLP.get(lp);
        this.filterSupportedTokens = (tokens) => tokens.filter((el) => !this.pairsByToken.has(el));
        this.reinitializePairs = async (tokenWhitelist) => {
            this.pairsByRegistry = new Map();
            const pairsAll = await (0, async_1.concurrentMap)(5, this.registries, (r) => r.findPairs(tokenWhitelist).then((pairs) => {
                this.pairsByRegistry.set(r.getName(), pairs);
                return pairs;
            }));
            this.pairs = [];
            this.pairsByToken = new Map();
            pairsAll.forEach((pairs) => {
                pairs.forEach((p) => {
                    this.pairs.push(p);
                    for (const token of [p.tokenA, p.tokenB]) {
                        const x = this.pairsByToken.get(token);
                        if (x) {
                            x.push(p);
                        }
                        else {
                            this.pairsByToken.set(token, [p]);
                        }
                    }
                });
            });
            this.multiCallData = undefined;
            this.multiCallPayloadSlice = undefined;
            this.multicallIntervals = [];
            return this.pairs;
        };
        this.refreshPairs = async () => {
            await (0, async_1.concurrentMap)(10, this.pairs, (p) => p.refresh());
            return this.pairs;
        };
        this.getDepositAmount = (lp, inputAmount, previousRoutes) => {
            let pair = this.pairsByLP.get(lp);
            (0, tiny_invariant_1.default)(pair, `Pair does not exist for lp ${lp}`);
            for (const route of previousRoutes !== null && previousRoutes !== void 0 ? previousRoutes : []) {
                route.pairs.forEach((pair, i) => {
                    if (pair.pairKey) {
                        pair = pair.reflectLiquidityChanges(route.pathAmounts.slice(i, i + 2));
                    }
                });
            }
            return pair.depositAmount(inputAmount[0], inputAmount[1]);
        };
        this.getPairsWithLP = () => Array.from(this.pairsByLP.values());
        this.getWithdrawAmount = (lp, amount) => { var _a; return (_a = this.pairsByLP.get(lp)) === null || _a === void 0 ? void 0 : _a.withdrawAmount(amount); };
        this.findBestRoutesForFixedInputAmount = (inputToken, outputToken, inputAmount, opts) => {
            return (0, router_1.findBestRoutesForFixedInputAmount)(this.pairsByToken, inputToken, outputToken, inputAmount, opts);
        };
    }
    get allPairs() {
        return this.pairs;
    }
    getPairsByRegistry(registry) {
        return this.pairsByRegistry.get(registry) || [];
    }
    getPairDescriptors() {
        return this.pairs
            .map((el) => el.getDescriptor())
            .filter(({ _type }) => _type !== "general");
    }
}
exports.SwappaManager = SwappaManager;
const swapTX = (kit, routerAddr, route, inputAmount, minOutputAmount, to, opts) => {
    const router = new kit.web3.eth.Contract(SwappaRouterV1_1.ABI, routerAddr);
    const routeData = route.pairs.map((p, idx) => p.swapData(route.path[idx]));
    const deadlineMs = (opts === null || opts === void 0 ? void 0 : opts.deadlineMs) || Date.now() / 1000 + 60;
    const swapF = (opts === null || opts === void 0 ? void 0 : opts.precheckOutputAmount)
        ? router.methods.swapExactInputForOutputWithPrecheck
        : router.methods.swapExactInputForOutput;
    const tx = (0, connect_1.toTransactionObject)(kit.connection, swapF(route.path, routeData.map((d) => d.addr), routeData.map((d) => d.extra), inputAmount.toFixed(0), minOutputAmount.toFixed(0), to, deadlineMs.toFixed(0)));
    return tx;
};
exports.swapTX = swapTX;
//# sourceMappingURL=swappa-manager.js.map